import React, { useState, useEffect } from "react";
import { Filter, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { TopicMultiSelect } from "@/components/shared/TopicMultiSelect";

interface FlashcardFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  starredOnly: boolean;
  selectedConcepts: string[];
  availableTopics: string[];
  onApply: (starredOnly: boolean, selectedConcepts: string[]) => void;
}

const FlashcardFilterDialog: React.FC<FlashcardFilterDialogProps> = ({
  open,
  onOpenChange,
  starredOnly,
  selectedConcepts,
  availableTopics,
  onApply,
}) => {
  const [localStarred, setLocalStarred] = useState(starredOnly);
  const [localConcepts, setLocalConcepts] = useState<string[]>(selectedConcepts);

  useEffect(() => {
    if (open) {
      setLocalStarred(starredOnly);
      setLocalConcepts(selectedConcepts);
    }
  }, [open, starredOnly, selectedConcepts]);

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    onApply(localStarred, localConcepts);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-medium">
            <Filter className="h-5 w-5" />
            Filter Options
          </DialogTitle>
        </DialogHeader>

        <form className="mt-1 space-y-6" onSubmit={handleApply}>
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border p-2 px-3">
                <div className="flex items-center gap-3">
                  <Star className="h-4 w-4 text-muted-foreground" />
                  <label
                    htmlFor="starred-only"
                    className="cursor-pointer text-sm font-medium text-muted-foreground"
                  >
                    Starred only
                  </label>
                </div>
                <Switch
                  id="starred-only"
                  checked={localStarred}
                  onCheckedChange={setLocalStarred}
                  className="h-4 w-8 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-4"
                />
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="flex items-center gap-2 text-sm font-medium">
                Filter by Key concepts
                {localConcepts.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({localConcepts.length} selected)
                  </span>
                )}
              </h4>

              <TopicMultiSelect
                availableTopics={availableTopics}
                selectedTopics={localConcepts}
                onChange={setLocalConcepts}
                placeholder="Selected key concepts"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Apply</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FlashcardFilterDialog;
